import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
// @indipia/ui-core (src/index.ts) 가 export 하는 실제 컴포넌트를 직접 호출해 한 화면에 전시.
// 스타일은 components.css 의 의미 클래스(토큰 구동)를 컴포넌트가 알아서 사용한다.
import {
  Button, Input, Select, Textarea, Switch, Checkbox, Radio, Search, Modal, Card,
  Badge, ChoiceCard, Tag, Alert, Progress, Avatar, AvatarGroup, Divider, Skeleton, Tooltip,
  Tabs, Segmented, Breadcrumb, Steps, Pagination, Accordion, Counter, FilterChip,
  SearchCombo, Combobox, MultiSelect, KeywordForm, CollapsibleSection, StartCard, StartCardList, CardList, ItemList, ItemListRow, StatusHint, Table, StageBar,
  EmptyState, ErrorState, Calendar, DatePicker, DateRangePicker, FileInput, Upload,
  PhotoGrid, PhotoStrip, PhotoThumb, CanvasPhotoRow, CanvasTextRow,
  IconSite, IconPhoto, IconError, IconCopy, IconShare, IconKakaoTalk, AppTile,
  Popover, ContextMenu, SignalDot, SignalDots, SignalLegend, ScrollTopButton,
  type DateRangeValue,
} from '@indipia/ui-core';
import { ShareLinkPopup, shareViaKakao } from '@indipia/shared-features';
import { DEMO_TILES } from './demoTiles';
import { LayoutSection } from './ui-standard/LayoutSection';

// 테이블(§15.27) 데모 행 — status 는 Badge 의 status prop 그대로
const TABLE_ROWS = [
  { site: '강남 리모델링', status: 'success', statusLabel: '진행중', owner: '김예나', amount: '12,400,000' },
  { site: '판교 상가', status: 'warning', statusLabel: '검토', owner: '김민수', amount: '8,900,000' },
  { site: '분당 창호', status: 'info', statusLabel: '안내', owner: '이서연', amount: '3,200,000' },
] as const;

/** 공유하기 팝업 데모 — fetchLink 는 가짜 지연 응답. 카카오톡은 VITE_KAKAO_JS_KEY 가 없으면 비활성 */
function ShareLinkPopupDemo() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'ok' | 'fail'>('ok');
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button variant="secondary" onClick={() => { setMode('ok'); setOpen(true); }}>공유하기 열기</Button>
      <Button variant="etc" onClick={() => { setMode('fail'); setOpen(true); }}>조회 실패 상태</Button>
      <ShareLinkPopup
        open={open}
        onClose={() => setOpen(false)}
        siteId="demo-site"
        fetchLink={() => new Promise((resolve, reject) => {
          setTimeout(() => (mode === 'ok'
            ? resolve({ url: 'https://example.invalid/c/demo-token', expiresNotice: '서버가 준 만료 안내 문구 자리' })
            : reject(new Error('demo'))), 600);
        })}
        notice="화면별 안내 문구 자리"
        onKakaoShare={(url) => { void shareViaKakao({ url, title: '우리집 공사 소식', imageUrl: '/vite.svg' }); }}
      />
    </div>
  );
}

/** §15.39 데모 — 다중선택 그리드 + 선택 스트립 */
function PhotoGridDemo() {
  const [sel, setSel] = useState<(number | string)[]>([1, 3]);
  const toggle = (id: number | string) =>
    setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const selected = DEMO_TILES.filter((t) => sel.includes(t.id));
  return (
    <div className="flex flex-col gap-3">
      <PhotoStrip
        items={selected}
        title={selected.length ? `선택된 사진 ${selected.length}장` : undefined}
        onRemove={toggle}
      />
      <PhotoGrid items={DEMO_TILES} selectedIds={sel} onToggle={toggle} maxHeight={260} />
    </div>
  );
}

/** §15.40 데모 — 블록 캔버스(사진 행 + 종속 문단 행) */
function BlockCanvasDemo() {
  const [cols, setCols] = useState(2);
  const [caption, setCaption] = useState('');
  const [text, setText] = useState('');
  const photos = DEMO_TILES.slice(0, cols).map((t) => ({ id: t.id, url: t.url }));
  return (
    <div className="rounded-xxl border border-border bg-surface-raised p-6">
      <CanvasPhotoRow
        photos={photos}
        columns={cols}
        caption={caption}
        onColumns={(d) => setCols((c) => Math.max(1, Math.min(3, c + d)))}
        onRemovePhoto={() => {}}
        onDropPhoto={() => {}}
        onCaption={setCaption}
        onRemoveRow={() => {}}
        onDragStart={() => {}}
      />
      <CanvasTextRow value={text} onChange={setText} />
    </div>
  );
}

/** §15.55 데모 — 데모 상자를 스크롤 소유자로 넘겨 실제로 동작시킨다(기본은 position:fixed) */
function ScrollTopDemo() {
  const box = useRef<HTMLDivElement>(null);
  return (
    <div className="relative w-80">
      <div ref={box} className="h-44 overflow-y-auto rounded-xxl border border-border bg-surface p-3">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="mb-2 h-10 rounded-lg border border-border bg-surface-raised" />
        ))}
      </div>
      <ScrollTopButton scrollRef={box} threshold={60} style={{ position: 'absolute', right: 20, bottom: 20 }} />
    </div>
  );
}

/** §15.11 데모 — Popover(앵커형 자유 컨텐츠) · ContextMenu(우클릭 좌표 + 하위 메뉴) */
function OverlayAnchorDemo() {
  const anchor = useRef<HTMLSpanElement>(null);
  const [popOpen, setPopOpen] = useState(false);
  const [ctx, setCtx] = useState<{ x: number; y: number } | null>(null);
  return (
    <div className="flex flex-wrap items-start gap-4">
      <Spec name="Popover" use="클릭 — 앵커 옆 자유 컨텐츠(폼·달력)">
        <span ref={anchor} className="inline-flex">
          <Button variant="secondary" onClick={() => setPopOpen((v) => !v)}>팝오버 (§15.11) ▾</Button>
        </span>
      </Spec>
      <Popover open={popOpen} onClose={() => setPopOpen(false)} anchorRef={anchor} width={240} aria-label="담당자 지정">
        <p className="mb-3 text-sm">자유 컨텐츠 — 목록이 아닌 폼·달력을 담는다.</p>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="etc" onClick={() => setPopOpen(false)}>취소</Button>
          <Button size="sm" onClick={() => setPopOpen(false)}>지정</Button>
        </div>
      </Popover>
      <Spec name="ContextMenu" use="우클릭 좌표 — 액션 목록 · items 있으면 하위 메뉴 · danger 항목">
        <div
          className="flex h-16 w-64 items-center justify-center text-xs text-text-secondary"
          style={{ border: '1px dashed var(--shell-border)', borderRadius: 'var(--radius-md)' }}
          onContextMenu={(e) => { e.preventDefault(); setCtx({ x: e.clientX, y: e.clientY }); }}
        >
          여기서 우클릭 (§15.11)
        </div>
      </Spec>
      <ContextMenu
        open={!!ctx}
        x={ctx?.x ?? 0}
        y={ctx?.y ?? 0}
        onClose={() => setCtx(null)}
        aria-label="현장 작업"
        heading="강남 리모델링"
        items={[
          { key: 'rename', label: '✏️ 이름 바꾸기' },
          {
            key: 'move',
            label: '📁 이동',
            items: [
              { key: 'ing', label: '진행 중' },
              { key: 'keep', label: '보관' },
            ],
          },
          { key: 'del', label: '🗑 삭제', danger: true },
        ]}
      />
    </div>
  );
}

/** §15.50 데모 — KeywordForm 3상태(readonly·첫 로딩·태그 추가됨) + 옵션(드래그·모두 삭제·칩 클릭·정해진 목록) */
function KeywordFormDemo() {
  const [tags, setTags] = useState(['인테리어태그', '조명공사', '부엌공사']);
  const [empty, setEmpty] = useState<string[]>([]);
  const [opt, setOpt] = useState(['거실']);
  const [clicked, setClicked] = useState('');
  const add = (set: typeof setTags) => (v: string) => set((t) => [...t, v]);
  const del = (set: typeof setTags) => (i: number) => set((t) => t.filter((_, j) => j !== i));
  const move = (from: number, to: number) => setTags((t) => { const n = [...t]; n.splice(to, 0, n.splice(from, 1)[0]); return n; });
  return (
    <div className="grid max-w-xl gap-6">
      <KeywordForm mode="readonly" tags={tags} onRemove={del(setTags)} help="readonly — 칩만, × 유지" />
      <KeywordForm tags={empty} onAdd={add(setEmpty)} onRemove={del(setEmpty)} help="첫 로딩 시 — Enter 추가 · 빈 입력 Backspace 삭제 · 중복 경고" />
      <KeywordForm
        tags={tags} onAdd={add(setTags)} onRemove={del(setTags)} maxCount={8}
        onReorder={move} onClear={() => setTags([])} onTagClick={(t) => setClicked(t)}
        help={clicked ? `칩 클릭: ${clicked}` : '태그 추가된 상태 — 드래그 순서변경 · 칩 클릭 · 모두 삭제 · 최대 8개'}
      />
      <KeywordForm
        tags={opt} onAdd={add(setOpt)} onRemove={del(setOpt)} allowCustom={false}
        options={['욕실', '침실', '거실', '주방', '현관', '베란다']} placeholder="공간 선택"
        help="정해진 목록(allowCustom=false) — 목록 밖 값은 경고"
      />
    </div>
  );
}

// MultiSelect 데모 데이터 (§15.47) — 정적 목록 전용
const TRADES = [
  { value: 'frame', label: '골조' },
  { value: 'finish', label: '마감' },
  { value: 'elec', label: '전기' },
  { value: 'plumb', label: '설비' },
  { value: 'paint', label: '도장' },
];

// MultiSelect 칩 옵션 데모 데이터 (§15.52) — 사업장 직원 · 사람별 알림 받기
const STAFF = [
  { value: 'u1', label: '김민수' },
  { value: 'u2', label: '이서준' },
  { value: 'u3', label: '박지훈' },
  { value: 'u4', label: '최유나' },
];

// Combobox 데모 데이터 — searchable 정적 목록 · loadOptions 비동기 시뮬레이션(800ms)
const PARTNERS = [
  { value: 'p1', label: '김철수 반장' },
  { value: 'p2', label: '김영호 반장' },
  { value: 'p3', label: '박민준 반장' },
  { value: 'p4', label: '이정도 반장' },
  { value: 'p5', label: '최수현 반장' },
  { value: 'p6', label: '한가람 반장' },
];
const VENDORS = [
  { value: 'v1', label: '(주)한샘자재' },
  { value: 'v2', label: '대림바스몰' },
  { value: 'v3', label: 'LX하우시스 대리점' },
  { value: 'v4', label: '동화자연마루 총판' },
];
const loadVendors = (query: string) =>
  new Promise<typeof VENDORS>((resolve) => {
    setTimeout(() => {
      resolve(VENDORS.filter((v) => v.label.toLowerCase().includes(query.toLowerCase())));
    }, 800);
  });

// 토스트 데모 — 클릭마다 success→info→warning 순환 (design-system.html §14.8 데모와 동일)
const TOAST_KINDS: [string, string, string][] = [
  ['success', '✓', '저장되었습니다.'],
  ['info', 'ⓘ', '자재 단가가 갱신되었습니다.'],
  ['warning', '⚠', '발주 기한 임박.'],
];


// 데모 섹션 래퍼 (레이아웃은 표준 Tailwind = 토큰 별칭)
function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="mb-4 border-b border-border pb-2 text-lg font-bold text-point">
        {title}
        {hint && <span className="ml-2 text-xs font-normal text-text-secondary">{hint}</span>}
      </h2>
      {children}
    </section>
  );
}
function Row({ top, children }: { top?: boolean; children: ReactNode }) {
  return (
    <div
      className={`flex flex-wrap ${top ? 'items-start' : 'items-center'} gap-3 rounded-xxl border border-border bg-surface-raised p-4`}
    >
      {children}
    </div>
  );
}
function Sub({ children }: { children: ReactNode }) {
  return <h3 className="mb-2 mt-8 text-xs font-semibold uppercase tracking-wide text-text-secondary first:mt-0">{children}</h3>;
}
/** Row 와 같은 박스지만 전폭 블록(세로 쌓기) — 표·탭·리스트처럼 폭을 다 쓰는 데모를 묶는다 */
function Box({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-3 rounded-xxl border border-border bg-surface-raised p-4">{children}</div>;
}
/** Box 안 변형 이름표 — 같은 컴포넌트의 옵션·변형을 구분한다 */
function Cap({ children }: { children: ReactNode }) {
  return <code className="text-xs text-text-secondary">{children}</code>;
}
/** Row 안 한 칸 — 데모 + 옵션/상태 이름(code) + 쓰임새. 같은 컴포넌트의 변형을 한 박스에서 비교하게 한다 */
function Spec({ name, use, children }: { name: string; use?: string; children: ReactNode }) {
  return (
    <div className="flex min-w-13 flex-col items-center gap-2">
      <div className="flex min-h-08 items-center">{children}</div>
      <code className="text-xs">{name}</code>
      {use && <span className="text-center text-xs text-text-secondary">{use}</span>}
    </div>
  );
}

// data-group 값 체계는 표준 D-07 §5.2 를 그대로 따른다 — 01 사진관리 / 02 견적 / 03 일정관리 / 04 창호.
// (구버전은 라벨이 한 칸 밀려 01=기본·02=사진 이었다.)
// 현재 01~04 는 전부 기본 primary(neutral.500)로 대치되어 있어 색이 같다 — 그룹별 색 배정은 추후 확정.
const GROUPS = [
  { label: '01 사진관리', value: '01' },
  { label: '02 견적', value: '02' },
  { label: '03 일정관리', value: '03' },
  { label: '04 창호', value: '04' },
];

/**
 * UI 표준 — @indipia/ui-core 컴포넌트 라이브 갤러리.
 * 앱 LNB "UI 표준"에서 진입. 색상/모드 스위처로 토큰 구동을 직접 확인할 수 있다.
 */
export function UiStandardPage() {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [group, setGroup] = useState('01');

  const [tab, setTab] = useState('a');
  const [ptab, setPtab] = useState('all');
  const [seg, setSeg] = useState('list');
  const [page, setPage] = useState(2);
  const [qty, setQty] = useState(3);
  const [on, setOn] = useState(true);
  const [q, setQ] = useState('');
  const [cq, setCq] = useState('');
  const [openA, setOpenA] = useState(true);
  const [openB, setOpenB] = useState(true);
  const [modal, setModal] = useState(false);
  const [tableSel, setTableSel] = useState<string>('판교 상가');
  const [tableOpen, setTableOpen] = useState<ReadonlySet<string | number>>(() => new Set(['판교 상가']));
  const [modalSize, setModalSize] = useState<'sm' | 'md' | 'lg' | 'xl' | 'full'>('md');
  const [files, setFiles] = useState<File[]>([]);
  const [files2, setFiles2] = useState<File[]>([]); // renderEmpty 예시용
  const [chips, setChips] = useState<Record<string, boolean>>({ all: true });
  const [partner, setPartner] = useState('');
  const [trades, setTrades] = useState<string[]>(['frame', 'elec']);
  const [charges, setCharges] = useState<string[]>(['u1']);
  const [admins, setAdmins] = useState<string[]>(['u2', 'u3']);
  const [adminNoti, setAdminNoti] = useState<string[]>(['u2']);
  const [vendor, setVendor] = useState('v1'); // 이미 저장된 거래처 — 로딩 전 displayValue 라벨 유지 확인용
  const dateRef = useRef<HTMLInputElement>(null);
  const [toasts, setToasts] = useState<{ id: number; kind: string; icon: string; msg: string }[]>([]);
  const toastSeq = useRef(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dateVal, setDateVal] = useState('2026-06-17');
  const [range, setRange] = useState<DateRangeValue>({ start: new Date(2026, 4, 25), end: new Date(2026, 5, 27) });
  const [subChecks, setSubChecks] = useState([true, false, false]);

  const pushToast = () => {
    const [kind, icon, msg] = TOAST_KINDS[toastSeq.current % TOAST_KINDS.length];
    const id = ++toastSeq.current;
    setToasts((t) => [...t, { id, kind, icon, msg }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3000);
  };

  // 드롭다운 — 바깥 클릭 닫기
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuOpen]);

  return (
    <div
      data-theme={theme === 'dark' ? 'dark' : undefined}
      data-group={group}
      className="min-h-screen bg-surface p-6 text-text md:p-10"
    >
      {/* 헤더 + 스위처 */}
      <div className="mb-8">
        <div className="font-mono text-xs text-text-secondary">@indipia/ui-core · D-09 §15</div>
        <h1 className="mt-1 text-2xl font-bold">UI 표준 — 컴포넌트 갤러리</h1>
        <div className="mt-1 font-mono text-xs text-text-secondary">
          token.json v2.010 · 표준 D-07 / D-08 / D-09
        </div>
        <p className="mt-2 text-sm text-text-secondary">
          아래는 <code className="rounded bg-surface-raised px-1">src/index.ts</code> 가 export 하는 실제 컴포넌트입니다.
          색상/모드를 바꾸면 토큰(<code className="rounded bg-surface-raised px-1">--gt-*</code>·<code className="rounded bg-surface-raised px-1">data-theme</code>)만으로 전체가 반응합니다.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Segmented options={GROUPS} value={group} onChange={setGroup} />
          <Segmented
            options={[{ label: 'Light', value: 'light' }, { label: 'Dark', value: 'dark' }]}
            value={theme}
            onChange={(v) => setTheme(v as 'light' | 'dark')}
          />
        </div>
      </div>

      <LayoutSection />

      <Section title="Typography" hint="§9 (v2.015 제목 계단 22/18/16)">
        <p className="mb-3 text-sm text-text-secondary">
          모바일·웹 동일 스케일. lineHeight 는 size 대비 배율, weight 는 스케일이 소유한다.
          Tailwind 유틸(<code className="rounded bg-surface-raised px-1">text-heading-md</code> 등)이 세 값을 함께 적용한다.
          <br />
          <b>12px 하한</b> — 배지·태그를 포함해 화면 텍스트는 12px 미만을 쓰지 않는다(인쇄·PDF export 문자열은 예외).
          <br />
          <b>deprecated</b> — <code className="rounded bg-surface-raised px-1">text-title-lg</code>/<code className="rounded bg-surface-raised px-1">-md</code> → <code className="rounded bg-surface-raised px-1">text-heading-lg</code>,{' '}
          <code className="rounded bg-surface-raised px-1">text-title-sm</code> → <code className="rounded bg-surface-raised px-1">text-heading-md</code> 별칭으로만 남아 있다. 신규 코드에서 쓰지 않는다.
          <code className="rounded bg-surface-raised px-1">text-body-lg</code> 는 <b>삭제</b>됐다 — <code className="rounded bg-surface-raised px-1">text-heading-sm</code>(값 동일)을 쓴다.
        </p>
        <Box>
          <div className="overflow-x-auto">
            {/* 폭은 내용이 정한다 — 매직넘버 min-width 대신 헤더 nowrap + 래퍼 overflow-x-auto 로 처리 */}
            <table className="w-full border-collapse whitespace-nowrap text-left">
              <thead>
                <tr className="border-b border-border text-caption text-text-secondary">
                  <th className="py-2 pr-4 font-semibold">토큰 / 유틸</th>
                  <th className="py-2 pr-4 font-semibold">size · line · weight</th>
                  <th className="py-2 pr-4 font-semibold">용도</th>
                  <th className="py-2 font-semibold">샘플</th>
                </tr>
              </thead>
              <tbody>
                {([
                  ['display-lg', '56 · 120% · 600', '대형 랜딩 타이틀, 메인 비주얼 카피'],
                  ['display-sm', '32 · 100% · 600', '대시보드 point, 페이지 상단 인사말'],
                  ['heading-lg', '22 · 100% · 600', '페이지 제목'],
                  ['heading-md', '18 · 100% · 600', '섹션 제목'],
                  ['heading-sm', '16 · 100% · 600', '리스트(아이템) 제목, 모달 타이틀'],
                  ['body-md', '16 · 130% · 400', '기본, 본문'],
                  ['body-sm', '14 · 130% · 400', '모달 본문'],
                  ['caption', '12 · 100% · 400', '라벨, 캡션, 메타 정보, unit, 안내 (본문 최저선)'],
                ] as const).map(([tok, spec, use]) => (
                  <tr key={tok} className="border-b border-border/60 align-middle">
                    <td className="py-2.5 pr-4 font-mono text-caption text-text">{tok}</td>
                    <td className="py-2.5 pr-4 font-mono text-caption text-text-secondary">{spec}</td>
                    <td className="py-2.5 pr-4 text-caption text-text-secondary">{use}</td>
                    <td
                      className="py-2.5"
                      style={{
                        fontSize: `var(--type-${tok}-size)`,
                        lineHeight: `var(--type-${tok}-line)`,
                        fontWeight: `var(--type-${tok}-weight)` as unknown as number,
                      }}
                    >
                      가나다 Ag
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Box>
        <Sub>border width (§6.12) — 1.5px 등 스케일 밖 두께는 hairline(1px)으로 스냅한다</Sub>
        <Row>
          {(['hairline', 'base', 'thick'] as const).map((k) => (
            <div key={k} className="flex flex-col items-center gap-1">
              <div
                className="h-10 w-10 rounded-sm"
                style={{ border: `var(--border-width-${k}) solid var(--shell-on-surface)` }}
              />
              <span className="font-mono text-caption text-text-secondary">{k}</span>
            </div>
          ))}
        </Row>
      </Section>

      <Section title="Button" hint="§14.1">
        <Row>
          <Button>primary</Button>
          <Button variant="secondary">secondary</Button>
          <Button variant="etc">etc</Button>
          <Button variant="ghost">ghost</Button>
          <Button variant="danger">danger</Button>
          <Button variant="success">success</Button>
          <Button disabled>disabled</Button>
          <Button loading>loading</Button>
          <Button className="btn-icon" aria-label="추가">＋</Button>
        </Row>
        <Sub>size</Sub>
        <Row>
          <Button size="sm">sm</Button>
          <Button>md (기본)</Button>
          <Button size="lg">lg</Button>
        </Row>
      </Section>

      <Section title="Form" hint="§14.2·14.12·14.13">
        <Row top>
          <Input label="현장명" placeholder="예: 강남 리모델링" />
          <Select id="uis-form-trade" label="공정"><option>견적</option><option>일정관리</option><option>창호</option></Select>
          <Input id="uis-form-phone" label="연락처" error="형식 오류" defaultValue="010-0000" />
        </Row>
        <Sub>size (sm/md/lg) · width (§2-10: default 320 / fill / auto)</Sub>
        <Row top>
          <Input size="sm" placeholder="sm" />
          <Input placeholder="md (기본)" />
          <Input size="lg" placeholder="lg" />
          <div style={{ display: 'flex', gap: 12, flex: 1 }}>
            <Input width="fill" placeholder="width=fill (flex 채움)" />
            <Input width="auto" placeholder="width=auto (320 해제)" />
          </div>
        </Row>
        <Sub>textarea · switch · check · radio · search</Sub>
        <Row top>
          <Textarea label="메모" placeholder="내용을 입력하세요" />
          <Switch checked={on} onChange={(e) => setOn(e.target.checked)} aria-label="switch 데모" />
          <Checkbox label="알림 받기" defaultChecked />
          <Radio name="r" label="견적" defaultChecked />
          <Radio name="r" label="창호" />
          <Search value={q} onChange={setQ} placeholder="현장 검색" width="default" />
        </Row>
        <Sub>states — disable · error · success (hover·focus는 인터랙션으로 확인)</Sub>
        <Row top>
          <Input id="uis-state-disabled" label="비활성 input" defaultValue="disable" disabled />
          <Select id="uis-state-disabled-select" label="비활성 select" disabled><option>select</option></Select>
          <Select id="uis-state-error-select" label="오류 select" error="필수 항목"><option>공정 선택</option></Select>
          <Input id="uis-state-email" label="이메일" success="✓ 사용 가능" defaultValue="user@indipia.com" />
          <Search value="" onChange={() => {}} aria-label="검색 비활성 상태" placeholder="비활성" disabled width="default" />
          <Search value="검색어" onChange={() => {}} aria-label="검색 오류 상태" error="결과 없음" width="default" />
        </Row>
        <Sub>search width (§15.13) — 기본은 fill (Input 은 default 320). SearchCombo 내부에서는 조합이 폭을 소유해 적용되지 않는다</Sub>
        <Row top>
          <Search value={q} onChange={setQ} placeholder='width="default" — 320 상한' width="default" />
          <Search value={q} onChange={setQ} placeholder='width="auto" — 내용폭' width="auto" />
          {/* 검색 옆 인접 버튼은 조합과 같은 규격 — gap n4 · primary (§15.13) */}
          <div className="w-full">
            <div className="search-combo rounded-xxl border border-dashed border-border p-3">
              <Search value={q} onChange={setQ} placeholder='width="fill" (기본) — 남는 폭을 모두 차지' />
              <Button variant="primary">인접 버튼</Button>
            </div>
          </div>
        </Row>
        <Sub>switch states — off · on · disable·off · disable·on (활성 pointer / 비활성 not-allowed)</Sub>
        <Row>
          <Switch checked={false} readOnly aria-label="switch off (활성)" />
          <Switch checked readOnly aria-label="switch on (활성)" />
          <Switch checked={false} readOnly disabled aria-label="switch off (비활성)" />
          <Switch checked readOnly disabled aria-label="switch on (비활성) — 잠긴 켜짐" />
        </Row>
        <Sub>sizes — sm / md(base) / lg (control-height, D-08 §6.10)</Sub>
        <Row top>
          <Input label="sm" size="sm" placeholder="sm" />
          <Input label="md (base)" placeholder="md" />
          <Input label="lg" size="lg" placeholder="lg" />
          <Select id="uis-size-select-sm" label="select sm" size="sm"><option>sm</option></Select>
          <Select id="uis-size-select-lg" label="select lg" size="lg"><option>lg</option></Select>
        </Row>
        <Sub>search combo (§15.13) — 클릭·Enter 검색, sm·md·lg 동반</Sub>
        <Row top>
          <SearchCombo value={cq} onChange={setCq} onSearch={() => {}} placeholder="현장 검색" />
          <SearchCombo size="sm" value={cq} onChange={setCq} onSearch={() => {}} placeholder="sm" />
          <SearchCombo size="lg" value={cq} onChange={setCq} onSearch={() => {}} placeholder="lg" />
        </Row>
        <Sub>combobox (§15.36 신규) — 타이핑 필터 · 비동기 로딩(선택 라벨 유지)</Sub>
        <Row top>
          <Combobox
            id="uis-combo-partner"
            label="반장님 (searchable 오토컴플리트)"
            searchable
            options={PARTNERS}
            value={partner}
            onChange={setPartner}
            placeholder="이름으로 검색"
            className="w-60"
          />
          <Combobox
            id="uis-combo-vendor"
            label="자재 거래처 (비동기 800ms · displayValue)"
            loadOptions={loadVendors}
            value={vendor}
            onChange={setVendor}
            displayValue={VENDORS.find((v) => v.value === vendor)?.label}
            className="w-60"
          />
        </Row>
        <p className="mt-2 text-xs text-text-secondary">
          거래처 콤보는 열기 전·로딩 중에도 저장된 선택 라벨("(주)한샘자재")이 유지됩니다. 검색어를 "zz"로 입력하면 빈 결과 문구가 나옵니다.
        </p>
        <Sub>다중 선택 MultiSelect (§15.47 신규) — 칩으로 쌓이고 메뉴는 닫히지 않는다</Sub>
        <Row top>
          <MultiSelect
            label="공종 (기본)"
            options={TRADES}
            values={trades}
            onChange={setTrades}
            placeholder="공종을 고르세요"
            className="w-60"
          />
          <MultiSelect
            label="공종 (searchable)"
            searchable
            options={TRADES}
            values={trades}
            onChange={setTrades}
            placeholder="공종 검색"
            className="w-60"
          />
          <MultiSelect label="disabled" options={TRADES} values={trades} onChange={setTrades} disabled className="w-40" />
        </Row>
        <p className="mt-2 text-xs text-text-secondary">
          두 컨트롤이 같은 상태를 공유합니다 — 한쪽에서 고르면 다른 쪽 칩도 같이 바뀝니다. 칩의 ×, 목록 항목 재클릭,
          searchable 에서 검색어가 빈 채로 누르는 Backspace 셋 다 선택을 지웁니다.
        </p>
        <Sub>태그·키워드 입력 KeywordForm (§15.50) — MultiSelect 와 비교해 고르기</Sub>
        <Row top>
          <KeywordFormDemo />
        </Row>
        <p className="mt-2 text-xs text-text-secondary">
          고르는 기준 — 필터·폼 필드에서 체크박스 목록으로 값을 고르면 MultiSelect, 태그·키워드를 칩으로 입력·관리하면 KeywordForm
          (직접 입력이 기본이고, 정해진 목록만 허용할 때는 options + allowCustom=false). 둘 다 칩 × · 빈 입력 Backspace 로 지웁니다.
        </p>
        <Sub>MultiSelect 칩 옵션 (§15.52) — toggled(칩 토글 아이콘) · removable(칩 ×), 쓸지 말지 고른다</Sub>
        <Row top>
          <div className="w-60">
            <MultiSelect
              label="현장 담당자 (MultiSelect)"
              searchable
              options={STAFF}
              values={charges}
              onChange={setCharges}
              placeholder="직원을 선택하세요"
            />
            <p className="mt-1 text-xs text-text-secondary">담당자는 알림을 항상 받습니다.</p>
          </div>
          <MultiSelect
            label="현장 관리자 (toggled)"
            searchable
            options={STAFF}
            values={admins}
            onChange={setAdmins}
            toggled={adminNoti}
            onToggledChange={setAdminNoti}
            placeholder="직원을 선택하세요"
            className="w-60"
          />
          <MultiSelect
            label="칩 × 없음 (removable={false})"
            options={STAFF}
            values={admins}
            onChange={setAdmins}
            removable={false}
            className="w-60"
          />
        </Row>
        <p className="mt-2 text-xs text-text-secondary">
          toggled 를 넘기면 칩 라벨 뒤에 토글 아이콘(기본 종 🔔/🔕)이 붙습니다. 아이콘을 누르면 그 값만 켜고 끄며 메뉴는 열리지
          않습니다. 새로 고른 값은 꺼진 채로 들어옵니다. removable={'{false}'} 면 칩 × 와 Backspace 삭제가 빠지고 목록 체크로만 해제합니다.
          끌 수 없는 값(예: 현장 담당자는 알림 항상 받음)은 토글 없이 안내 문구로 둡니다.
        </p>
        <Sub>날짜 선택 DatePicker (§15.14 공용) — 클릭 시 캘린더 팝오버 · forwardRef</Sub>
        <Row top>
          <DatePicker label="발주일" value={dateVal} onChange={setDateVal} ref={dateRef} className="w-48" />
        </Row>
        <Sub>기간 선택 DateRangePicker (§15.14 공용) — 알약 트리거 · 2개월 팝오버 · 첫 클릭 시작일, 두 번째 클릭 종료일</Sub>
        <Row>
          <Spec name='size="sm"' use="높이 32"><DateRangePicker size="sm" value={range} onChange={setRange} aria-label="기간 선택 sm" /></Spec>
          <Spec name="size md (기본)" use="높이 36"><DateRangePicker value={range} onChange={setRange} aria-label="기간 선택 md" /></Spec>
          <Spec name='size="lg"' use="높이 48"><DateRangePicker size="lg" value={range} onChange={setRange} aria-label="기간 선택 lg" /></Spec>
        </Row>
        <Sub>DateRangePicker — 상태 · 옵션</Sub>
        <Row>
          <Spec name="active" use="프리셋 칩과 함께 둘 때 — 지금 조회를 지배"><DateRangePicker value={range} onChange={setRange} active aria-label="적용 중 표시" /></Spec>
          <Spec name="presets={false}" use="팝오버 빠른선택 없음"><DateRangePicker value={range} onChange={setRange} presets={false} aria-label="빠른선택 없음" /></Spec>
          <Spec name="disabled" use="빈 값 · 비활성"><DateRangePicker value={{ start: null, end: null }} onChange={() => {}} disabled aria-label="disabled" /></Spec>
        </Row>
        <p className="mt-2 text-xs text-text-secondary">
          높이만 입력과 같은 <code>control-height</code>(sm 32 · md 36 · lg 48)를 따르고 모서리는 알약입니다 — 목록 조회조건 바에서 필터칩과 한 행을 이루는 컨트롤이라 단일 날짜 <code>DatePicker</code>(<code>radius-md</code>)와 다릅니다.
          팝오버는 폭을 지정하지 않습니다. <code>active</code> 는 옆에 기간 프리셋 칩이 함께 놓이는 화면에서 "지금 조회를 지배하는 게 이쪽"임을 표시할 때만 씁니다.
        </p>
        <Sub>textarea fill (2-1) — 고정 높이 박스를 세로로 가득 채움</Sub>
        <Row top>
          <Textarea label="기본 (min-height 88)" placeholder="세로 resize 가능" />
          <div className="flex h-40 w-72 flex-col rounded-xxl border border-dashed border-border p-2">
            <Textarea fill label="fill (풀높이)" placeholder="박스 높이를 가득 채움 · resize 없음" />
          </div>
        </Row>
        <Sub>switch flex-shrink (2-3b) — 좁은 행에서도 트랙 44px 유지</Sub>
        <Row top>
          <div className="flex w-64 items-center justify-between gap-2 rounded-xxl border border-border p-3">
            <span className="text-sm">발주 알림 — 설명이 긴 행에서도 스위치가 찌그러지지 않습니다</span>
            <Switch checked={on} onChange={(e) => setOn(e.target.checked)} aria-label="발주 알림" />
          </div>
        </Row>
        <Sub>checkbox color — 개별 색 지정(공종색 등, CSS 변수 --chk-color) · 미지정 시 기본색</Sub>
        <Row>
          <Checkbox label="기본" defaultChecked />
          <Checkbox label="목공" defaultChecked color="var(--status-warning-600)" />
          <Checkbox label="전기" defaultChecked color="var(--status-success-600)" />
          <Checkbox bare aria-label="도장" defaultChecked color="var(--status-danger-600)" />
        </Row>
        <Sub>checkbox — indeterminate 전체선택 · bare 선택행 (공용 Checkbox)</Sub>
        <Row top>
          <div className="flex flex-col gap-2">
            <span className="text-xs text-text-secondary">indeterminate — 일부만 선택되면 전체선택이 "─" 표시</span>
            <Checkbox
              label="전체 선택"
              indeterminate={subChecks.some(Boolean) && !subChecks.every(Boolean)}
              checked={subChecks.every(Boolean)}
              onChange={(e) => setSubChecks([e.target.checked, e.target.checked, e.target.checked])}
            />
            <div className="ml-6 flex flex-col gap-1">
              {['목공', '전기', '도장'].map((name, i) => (
                <Checkbox
                  key={name}
                  label={name}
                  checked={subChecks[i]}
                  onChange={(e) => setSubChecks((s) => s.map((v, j) => (j === i ? e.target.checked : v)))}
                />
              ))}
            </div>
          </div>
          <div className="flex w-72 flex-col gap-2">
            <span className="text-xs text-text-secondary">bare — 행 전체가 label인 선택행(label 중첩 없이 체크박스만 렌더)</span>
            <label className="flex cursor-pointer items-center justify-between rounded-xxl border border-border p-3">
              <span className="text-sm">강남 리모델링 — 행 어디를 눌러도 선택</span>
              <Checkbox bare />
            </label>
          </div>
        </Row>
        <Sub>월 캘린더 Calendar (§15.14 공용 — 월 이동·오늘·선택) · 파일 입력 (§2-4)</Sub>
        <Row top>
          <Calendar value={dateVal} onChange={setDateVal} />
          <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <FileInput accept="image/*" multiple maxSizeMB={10} onFiles={setFiles}>
              📎 파일 선택 (버튼형)
            </FileInput>
            <Upload
              accept="image/*"
              multiple
              maxSizeMB={10}
              maxCount={5}
              value={files}
              onChange={setFiles}
              hint="이미지 최대 5개 · 각 10MB 이하"
            />
            {/* renderEmpty — 비어 있을 때만 기본 문구를 대체. 트리거가 div role="presentation" 이라
                안에 버튼을 둬도 button-in-button 이 되지 않는다. */}
            <Upload
              accept=".dwg,.pdf"
              multiple
              maxSizeMB={20}
              value={files2}
              onChange={setFiles2}
              hint="DWG · PDF · 각 20MB 이하"
              renderEmpty={() => (
                <>
                  {/* 칩 80 안에는 48 아이콘이 들어간다(D-09 §15.17) — 이모지는 상속 font-size 라 80 박스에서 점처럼 보인다 */}
                  <span className="dropzone-chip"><IconSite /></span>
                  <div className="dropzone-text">
                    <span className="dropzone-title">현장 도면을 올려주세요</span>
                    <span className="dropzone-desc">도면을 올리면 현장별로 자동 정리됩니다</span>
                    <span className="dropzone-hint">DWG · PDF · 각 20MB 이하</span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      alert('샘플 도면 불러오기');
                    }}
                  >
                    샘플 도면 불러오기
                  </Button>
                </>
              )}
            />
          </div>
        </Row>
        <Sub>슬라이더 (§14.15)</Sub>
        <Row top>
          <input className="slider" type="range" aria-label="슬라이더 데모" min={0} max={100} defaultValue={65} />
        </Row>
      </Section>

      <Section title="Card" hint="§14.3">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Card thumb="🏠">
            <span className="ey">진행중</span>
            <h4>강남 아파트 리모델링</h4>
            <p>거실·주방 확장 공사.</p>
            <div className="meta"><Badge variant="soft">김예나</Badge><Badge status="success">완료 3</Badge></div>
          </Card>
          <Card thumb="📐">
            <span className="ey">검토대기</span>
            <h4>판교 상가 인테리어</h4>
            <p>바닥·조명 교체.</p>
            <div className="meta"><Badge variant="soft">김민수</Badge><Badge status="warning">검토</Badge></div>
          </Card>
          <Card thumb="🪟">
            <span className="ey">신규</span>
            <h4>분당 단독주택 창호</h4>
            <p>전체 창호 교체 요청.</p>
            <div className="meta"><Badge variant="soft">이서연</Badge><Badge status="info">안내</Badge></div>
          </Card>
        </div>
        <Sub>card list — variants (column · row · agent-card grid-fit)</Sub>
        <Box>
          <Cap>column — 세로 스택</Cap>
          <div className="card-list column">
            <article className="app-card"><div className="thumb">🏠</div><div className="body"><h4>세로 스택 (column)</h4><p>flex column, gap 16.</p></div></article>
            <article className="app-card"><div className="thumb">📐</div><div className="body"><h4>두번째 카드</h4><p>세로로 쌓임.</p></div></article>
          </div>
          <Cap>row — 가로 스크롤</Cap>
          <div className="card-list row">
            <article className="app-card" style={{ minWidth: 200 }}><div className="thumb">🪟</div><div className="body"><h4>가로 스크롤 (row)</h4><p>overflow-x auto.</p></div></article>
            <article className="app-card" style={{ minWidth: 200 }}><div className="thumb">🏗</div><div className="body"><h4>두번째</h4><p>가로 배치.</p></div></article>
          </div>
          <Cap>grid-fit — agent-card 격자</Cap>
          <div className="card-list grid-fit">
            <article className="app-card agent-card">
              <div className="body">
                <div className="agent-card-head">
                  <div style={{ width: 48, height: 48, borderRadius: 'var(--radius-lg)', background: 'var(--gt-subtle)', color: 'var(--gt-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>📷</div>
                  <div><h4 className="agent-tit">인디포토</h4><p className="subname">IndiPhoto</p></div>
                </div>
                <p className="agent-desc">현장 사진을 업로드하면 현장별로 자동 정리됩니다. 세 줄을 넘으면 말줄임 처리됩니다.</p>
                <p className="agent-cta">시작하기 →</p>
              </div>
            </article>
            <article className="app-card agent-card">
              <div className="body">
                <div className="agent-card-head">
                  <div style={{ width: 48, height: 48, borderRadius: 'var(--radius-lg)', background: 'var(--gt-subtle)', color: 'var(--gt-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>📊</div>
                  <div><h4 className="agent-tit">인디공사현황</h4><p className="subname">IndiStatus</p></div>
                </div>
                <p className="agent-desc">진행 중인 현장의 공정 단계별 현황을 한눈에 파악합니다.</p>
                <p className="agent-cta">시작하기 →</p>
              </div>
            </article>
          </div>
        </Box>
      </Section>

      <Section title="ChoiceCard" hint="신규 · 클릭 전용 선택 카드">
        <Row top>
          <div className="flex w-full max-w-[680px] gap-3.5">
            <ChoiceCard
              highlight
              badge={<Badge status="accent">추천</Badge>}
              title="견적서 올리기"
              description={<><strong>파일만 올리면 공종과 일정을 자동으로 만들어줘요</strong><br />가장 빠르고 쉬운 방법이에요</>}
              hint="엑셀 · PDF · 이미지 견적서를 다음 화면에서 올려요"
            />
            <ChoiceCard title="직접 입력하기" description="공종과 기간을 자유롭게 설정" hint="비활성 예시" disabled />
          </div>
        </Row>
      </Section>

      <Section title="Badge · Tag" hint="§14.5">
        <Row>
          <Badge variant="soft">soft</Badge>
          <Badge variant="outline">outline</Badge>
          <Badge status="success">진행중</Badge>
          <Badge status="warning">검토</Badge>
          <Badge status="danger">경고</Badge>
          <Badge status="info">안내</Badge>
          <Badge status="done">완료</Badge>
          <Badge status="mute">보류</Badge>
          <Badge status="accent">추천</Badge>
          <Tag>리모델링</Tag>
          <Tag onRemove={() => {}}>견적완료</Tag>
        </Row>
      </Section>

      <Section title="Alert" hint="§14.10">
        <Box>
          <div className="flex max-w-xl flex-col gap-2">
            <Alert kind="success" icon="✓">견적이 정상 저장되었습니다.</Alert>
            <Alert kind="warning" icon="⚠">발주 기한이 임박했습니다.</Alert>
            <Alert kind="error" icon="✕">네트워크 오류로 저장에 실패했습니다.</Alert>
            <Alert kind="info" icon="ⓘ">자재 단가는 매일 09:00 갱신됩니다.</Alert>
          </div>
        </Box>
      </Section>

      <Section title="Tabs · Segmented · FilterChip" hint="§14.19·14.21·14.18">
        <Sub>tabs (line)</Sub>
        <Box>
          <Tabs tabs={[{ label: '개요', value: 'a' }, { label: '상세', value: 'b' }, { label: '이력', value: 'c' }]} value={tab} onChange={setTab} />
        </Box>
        <Sub>tabs (pill) · segment</Sub>
        <Row>
          <Tabs variant="pill" tabs={[{ label: '전체', value: 'all' }, { label: '견적', value: 'est' }, { label: '발주', value: 'ord' }]} value={ptab} onChange={setPtab} />
          <Segmented options={[{ label: '리스트', value: 'list' }, { label: '보드', value: 'board' }, { label: '달력', value: 'cal' }]} value={seg} onChange={setSeg} />
        </Row>
        <Sub>segment sizes — control-height sm 32 / md 36 / lg 48 (Input과 공용 스케일, §15.21)</Sub>
        <Row>
          <Segmented size="sm" options={[{ label: 'sm', value: 'list' }, { label: '보드', value: 'board' }]} value={seg} onChange={setSeg} />
          <Segmented options={[{ label: 'md (base)', value: 'list' }, { label: '보드', value: 'board' }]} value={seg} onChange={setSeg} />
          <Segmented size="lg" options={[{ label: 'lg', value: 'list' }, { label: '보드', value: 'board' }]} value={seg} onChange={setSeg} />
        </Row>
        <Sub>filter chip</Sub>
        <Row>
          {[['all', '전체', 128], ['ing', '진행중', 42], ['done', '완료', 86]].map(([key, label, cnt]) => (
            <FilterChip
              key={key as string}
              active={!!chips[key as string]}
              count={cnt as number}
              onClick={() => setChips((c) => ({ ...c, [key as string]: !c[key as string] }))}
            >
              {label as string}
            </FilterChip>
          ))}
        </Row>
        <Sub>filter chip states — on/off 2상태만 쓴다 (tone·색 변형 없음 · Figma 2269-666). 상태색 구분은 아이콘·라벨이 담당</Sub>
        <Row>
          <FilterChip>off (normal)</FilterChip>
          <FilterChip active>on (active)</FilterChip>
          <FilterChip disabled>disabled</FilterChip>
          <FilterChip active disabled>active · disabled</FilterChip>
        </Row>
      </Section>

      <Section title="Breadcrumb · Steps · Pagination · Stepper" hint="§14.20·15.22·14.23·14.16">
        <Sub>breadcrumb</Sub>
        <Box>
          <Breadcrumb items={[{ label: '홈', href: '#' }, { label: '현장', href: '#' }, { label: '강남 리모델링' }]} />
        </Box>
        <Sub>steps — 1줄 라벨 (§15.22)</Sub>
        <Box>
          <Steps steps={['현장등록', '실측', '견적작성', '발주']} current={2} />
        </Box>
        <Sub>steps — 라벨 + 서브라벨 2줄 (§15.22, 구 §15.38)</Sub>
        <Box>
          <Steps
            steps={[
              { label: '사진업로드', sub: '3장 완료' },
              { label: '현장분류', sub: '현장 배정' },
              { label: '태그 등록', sub: '공간·공종 태깅' },
            ]}
            current={1}
          />
        </Box>
        <Sub>pagination · stepper</Sub>
        <Row>
          <Pagination page={page} pageCount={5} onChange={setPage} />
          <Counter size="sm" value={qty} onChange={setQty} />
          <Counter value={qty} onChange={setQty} />
          <Counter size="lg" value={qty} onChange={setQty} />
        </Row>
      </Section>

      <Section title="Accordion · Modal · Tooltip" hint="§14.25·14.6·14.9">
        <Accordion
          defaultOpen={[0]}
          items={[
            { title: '견적은 어떻게 작성하나요?', content: '현장 등록 → 실측 → 항목 입력 순으로 작성합니다.' },
            { title: '발주 타이밍 알림이란?', content: '자재 리드타임 기준으로 발주 시점을 알려줍니다.' },
          ]}
        />
        <Sub>modal · tooltip · toast · dropdown — z-index 토큰 적용(--z-overlay/--z-modal/--z-toast/--z-tooltip/--z-dropdown, D-08 §6.9)</Sub>
        <Row>
          <Button onClick={() => { setModalSize('md'); setModal(true); }}>모달 열기</Button>
          <Tooltip tip="포커스·hover로 노출됩니다">
            <Button variant="ghost">툴팁</Button>
          </Tooltip>
          <Button variant="etc" onClick={pushToast}>토스트 (§14.8)</Button>
          <div className="menu-anchor">
            <Button
              variant="secondary"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((v) => !v);
              }}
            >
              드롭다운 (§14.11) ▾
            </Button>
            <div className={menuOpen ? 'menu show' : 'menu'}>
              <div className="menu-item">✏️ 수정</div>
              <div className="menu-item">📄 복제</div>
              <div className="menu-sep" />
              <div className="menu-item danger">🗑 삭제</div>
            </div>
          </div>
        </Row>
        <Sub>앵커형 팝오버 · 우클릭 메뉴 (§15.11) — Popover 는 자유 컨텐츠, ContextMenu 는 우클릭 좌표 + 하위 메뉴. 둘 다 body 포탈이라 overflow 조상에 잘리지 않는다</Sub>
        <Row top>
          <OverlayAnchorDemo />
        </Row>
        <Sub>스크롤 맨 위로 (§15.55) — 기준선(threshold)을 넘으면 나타나고, 누르면 스크롤 소유자를 맨 위로 올린다. 데모는 상자 안에 가뒀지만 실제 기본값은 화면 우하단 고정이다</Sub>
        <Row top>
          <ScrollTopDemo />
        </Row>
        <Sub>dialog size (§2-11) — sm 240 · md 420(기본) · lg 640 · xl 880 · full 풀스크린</Sub>
        <Row>
          {(['sm', 'md', 'lg', 'xl', 'full'] as const).map((s) => (
            <Button key={s} variant="secondary" onClick={() => { setModalSize(s); setModal(true); }}>
              {s}
            </Button>
          ))}
        </Row>
        <Modal open={modal} onClose={() => setModal(false)} size={modalSize} title={`견적을 삭제할까요? (size: ${modalSize})`}>
          <p className="mb-4">삭제한 견적은 복구할 수 없습니다. 계속하시겠어요?</p>
          <div className="flex justify-end gap-2">
            <Button variant="etc" onClick={() => setModal(false)}>취소</Button>
            <Button variant="danger" onClick={() => setModal(false)}>삭제</Button>
          </div>
        </Modal>
      </Section>

      <Section title="Progress · Avatar · Divider · Skeleton" hint="§14.26·14.29·14.30">
        <Row>
          <Progress value={65} />
          <div className="prog-circle" style={{ '--p': '65%' } as CSSProperties}><span>65%</span></div>
          <AvatarGroup>
            <Avatar>박</Avatar>
            <Avatar>김</Avatar>
            <Avatar>이</Avatar>
            <Avatar>+5</Avatar>
          </AvatarGroup>
          <Avatar square>현</Avatar>
        </Row>
        <Sub>테이블 (§15.27) — 공용 Table(columns·rows). 행 클릭으로 선택행(.is-selected)</Sub>
        <Box>
          <Table
            columns={[
              { key: 'site', header: '현장', align: 'left' },
              { key: 'status', header: '상태', render: (r) => <Badge status={r.status}>{r.statusLabel}</Badge> },
              { key: 'owner', header: '담당' },
              { key: 'amount', header: '견적', align: 'right' },
            ]}
            rows={TABLE_ROWS}
            rowKey={(r) => r.site}
            selectedKey={tableSel}
            onRowClick={(r) => setTableSel(r.site)}
            empty="표시할 현장이 없습니다"
          />
        </Box>
        <Sub>테이블 펼침 행 · 단계 막대 — Table(renderExpanded·expandedKeys·stickyHeader), StageBar(total·done·tone)</Sub>
        <Box>
          <Table
            stickyHeader
            columns={[
              { key: 'site', header: '현장', align: 'left' },
              { key: 'stage', header: '진행', render: (r) => <StageBar total={4} done={r.site === '강남 리모델링' ? 2 : 1} tone={r.status === 'warning' ? 'warning' : 'default'} /> },
              { key: 'owner', header: '담당' },
            ]}
            rows={TABLE_ROWS}
            rowKey={(r) => r.site}
            expandedKeys={tableOpen}
            onRowClick={(r) =>
              setTableOpen((prev) => {
                const next = new Set(prev);
                if (next.has(r.site)) next.delete(r.site); else next.add(r.site);
                return next;
              })
            }
            renderExpanded={(r) => <StatusHint>{r.site} 펼침 영역 — 행 아래 전폭, 여러 행 동시 펼침</StatusHint>}
          />
        </Box>
        <Sub>신호등 점 · 범례 (D-08 §6.15 · §15.53) — 공종 진행 신호 전용. 점만으로 뜻을 전하지 않도록 이름을 곁들인다</Sub>
        <Row>
          <Spec name="SignalDots" use="반장 · 발주 · 작업 3점 한 줄 묶음">
            <SignalDots>
              <SignalDot color="YELLOW" />
              <SignalDot color="RED" />
              <SignalDot color="GREEN" />
            </SignalDots>
          </Spec>
          <Spec name='color="BLUE"' use="완료"><SignalDot color="BLUE" /></Spec>
          <Spec name='color="WHITE"' use="미시작"><SignalDot color="WHITE" /></Spec>
          <Spec name='color="NONE"' use="해당 없음"><SignalDot color="NONE" /></Spec>
          <Spec name="size={16} · label" use="크기 변경 · label 주면 role=img"><SignalDot color="GREEN" size={16} label="작업 신호 초록" /></Spec>
        </Row>
        <Sub>SignalLegend — title · caption · items · hint</Sub>
        <Row>
          <SignalLegend
          title="신호등"
          caption="반장 · 발주 · 작업 순"
          items={[
            { color: 'GREEN', label: '정상' },
            { color: 'YELLOW', label: '지연 주의' },
            { color: 'RED', label: '조치 필요' },
            { color: 'BLUE', label: '완료' },
            { color: 'WHITE', label: '미시작' },
            { color: 'NONE', label: '해당 없음' },
          ]}
          hint="숨김·표시는 호출부가 정한다(신호등 켠 때만)"
          />
        </Row>
        <Sub>리스트 (§14.28)</Sub>
        <Box>
          <div className="list">
            <div className="list-item"><span className="lead">🏠</span><div className="lbody"><div className="t">강남 아파트 리모델링</div><div className="s">실측 완료 · 견적 단계</div></div><span className="ltrail">›</span></div>
            <div className="list-item"><span className="lead">📐</span><div className="lbody"><div className="t">판교 상가 인테리어</div><div className="s">자재 단가 재검토</div></div><span className="ltrail">›</span></div>
          </div>
        </Box>
        <Sub>divider · skeleton (§14.30) — 구분선 · 카드 자리 로딩(circle + 줄)</Sub>
        <Box>
          <Divider />
          <div className="flex max-w-sm items-center gap-3">
            <Skeleton variant="circle" width="42px" height="42px" />
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton height="12px" width="70%" />
              <Skeleton height="10px" width="45%" />
            </div>
          </div>
        </Box>
        <Sub>skeleton variant — text(인라인) · circle</Sub>
        <Box>
          <p className="max-w-sm text-sm">
            오늘 등록 <Skeleton variant="text" width="3em" /> 건 · 완료 <Skeleton variant="text" width="2em" /> 건이 문장 흐름 속에 표시됩니다.
          </p>
        </Box>
      </Section>

      <Section title="대시보드 · 조합" hint="§15.31–35">
        <Sub>collapsible section + start card (§15.31·15.32)</Sub>
        <Box>
          <CollapsibleSection
            title="처리할 작업"
            date="2026년 7월 15일 화요일"
            open={openA}
            onToggle={() => setOpenA((v) => !v)}
          >
            <StartCardList>
              <StartCard icon="🖼" label="사진첩" count={4} unit="건" />
              <StartCard icon="📅" label="공사 스케줄" count={2} unit="건" />
              <StartCard icon="📊" label="현장 실적" count={10} unit="건" />
            </StartCardList>
          </CollapsibleSection>
        </Box>

        <Sub>collapsible section + item list (§15.31·15.33)</Sub>
        <Box>
          <CollapsibleSection
            title="최근 작업"
            notice={<StatusHint tone="attention" icon="ⓘ">최근 작업한 Data도 점점 서로 연결되고 있어요</StatusHint>}
            open={openB}
            onToggle={() => setOpenB((v) => !v)}
          >
            <ItemList>
              <ItemListRow
                title="박영희 님"
                hint={<StatusHint icon="📍">래미안 퍼스티지 84A</StatusHint>}
                link="인디포토 사진첩 1개 완성"
                trailing={<Badge status="info">진행중</Badge>}
              />
              <ItemListRow
                title="김민수 님"
                hint={<StatusHint icon="📍">판교 상가 인테리어</StatusHint>}
                link="공사 스케줄 3건 확정"
                trailing={<Badge status="info">진행중</Badge>}
              />
            </ItemList>
          </CollapsibleSection>
        </Box>

        <Sub>status hint (§15.34) — 아이콘만 status 색, 배경 없음</Sub>
        <Row>
          <div className="flex flex-col items-start gap-2">
            <StatusHint tone="attention" icon="ⓘ">지금 확인할 작업 34건이 기다리고 있어요 (attention)</StatusHint>
            <StatusHint tone="muted" icon="📍">래미안 퍼스티지 84A (muted)</StatusHint>
            <StatusHint tone="success" icon="✓">동기화가 완료되었습니다 (success)</StatusHint>
          </div>
        </Row>

        <Sub>card list (§15.35) — grid 컨테이너</Sub>
        <Box>
          <CardList>
            <Card thumb="🏠">
              <span className="ey">진행중</span>
              <h4>강남 아파트 리모델링</h4>
              <p>거실·주방 확장 공사.</p>
              <div className="meta"><Badge variant="soft">김예나</Badge><Badge status="success">완료 3</Badge></div>
            </Card>
            <Card thumb="📐">
              <span className="ey">검토대기</span>
              <h4>판교 상가 인테리어</h4>
              <p>바닥·조명 교체.</p>
              <div className="meta"><Badge variant="soft">김민수</Badge><Badge status="warning">검토</Badge></div>
            </Card>
            <Card thumb="🪟">
              <span className="ey">신규</span>
              <h4>분당 단독주택 창호</h4>
              <p>전체 창호 교체 요청.</p>
              <div className="meta"><Badge variant="soft">이서연</Badge><Badge status="info">안내</Badge></div>
            </Card>
          </CardList>
        </Box>
      </Section>

      <Section title="상태 UX" hint="§15.37 — Loading → Content / EmptyState / ErrorState (공용 컴포넌트)">
        <Box>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <EmptyState
              title="빈 상태 (Empty) — 위치정보"
              description="등록된 현장이 없습니다."
              action={<Button variant="secondary" size="sm">＋ 현장 만들기</Button>}
            />
            <EmptyState
              icon={<IconPhoto />}
              title="빈 상태 (Empty) — 사진"
              description="아직 선택한 사진이 없어요."
            />
            <ErrorState title="에러 (Error)" description="불러오기에 실패했습니다." onRetry={() => {}} />
            {/* 로딩 블록은 빈상태·에러와 같은 구조를 스켈레톤으로 모사한다 — 아이콘 자리는 skel-chip,
                실제 아이콘·이모지는 두지 않는다(D-07 §3.2 #7·#8). 버튼 자리는 그리지 않는다 */}
            <div className="state-block">
              <Skeleton variant="chip" />
              <div className="state-block-text">
                <h4>로딩 (Loading)</h4>
                <div className="flex flex-col gap-2">
                  <Skeleton height="12px" width="90%" />
                  <Skeleton height="12px" width="70%" />
                  <Skeleton height="12px" width="80%" />
                </div>
              </div>
            </div>
          </div>
        </Box>
        <p className="mt-2 text-xs text-text-secondary">
          전환: 요청→Loading → (데이터 있음 Content / 0건 Empty / 실패 Error). 로딩·빈 상태 혼용 금지, spinner 단독 금지, 5초↑ Error 전환.
        </p>

        <Sub>공용 아이콘 (D-07 §20) — viewBox 48×48 · stroke 3 · 색은 컨테이너가 currentColor 로 준다</Sub>
        <Row>
          {([
            ['IconSite', <IconSite key="s" />, 'EmptyState 기본값 — 위치·현장'],
            ['IconPhoto', <IconPhoto key="p" />, '사진'],
            ['IconError', <IconError key="e" />, 'ErrorState 기본값 — 실패·경고'],
          ] as const).map(([name, node, use]) => (
            /* 칩에 넣지 않고 원본 48 그대로 전시한다 — 아이콘 칩 규격은 아직 공용이 아니라
               (.ico-chip 은 .state-block 종속) 여기서 특정 칩을 쓰면 없는 규격을 암시하게 된다 */
            <div key={name} className="flex flex-col items-center gap-2" style={{ width: 160 }}>
              <span style={{ color: 'var(--gt-primary)', lineHeight: 0 }}>{node}</span>
              <code className="text-xs">{name}</code>
              <span className="text-xs text-text-secondary text-center">{use}</span>
            </div>
          ))}
        </Row>
        <p className="mt-2 text-xs text-text-secondary">
          크기 변경은 호출부에서 <code>width</code>/<code>height</code> 를 덮는다(<code>size</code> prop 없음). 색은 부모의 <code>color</code> 를 따르므로 칩·버튼 안에서 별도 지정이 필요 없다.
        </p>
      </Section>

      <Section title="이미지 선택 그리드" hint="§15.39 — PhotoGrid + PhotoStrip (다중선택, 표현 전용)">
        <PhotoGridDemo />
        <p className="mt-2 text-xs text-text-secondary">
          셀 클릭으로 선택 토글(✓ 배지·3px 보더), 선택 스트립에서 × 제거. 조회·필터는 소비처 소유.
        </p>
      </Section>

      <Section title="사진 썸네일 한 장" hint="PhotoThumb — 고정 정사각 미리보기 + 삭제 버튼 (승인된 신규 N-03)">
        <Row top>
          <Spec name='size="sm"' use="48 · onRemove(× 버튼)"><PhotoThumb size="sm" src="/vite.svg" alt="작은 썸네일" onRemove={() => undefined} /></Spec>
          <Spec name="size md (기본)" use="64 · onRemove + onOpen(원본 보기)"><PhotoThumb src="/vite.svg" alt="기본 썸네일" onRemove={() => undefined} onOpen={() => undefined} /></Spec>
          <Spec name='size="lg"' use="96 · 삭제 없음"><PhotoThumb size="lg" src="/vite.svg" alt="큰 썸네일" /></Spec>
          <Spec name="loading" use="불러오는 중"><PhotoThumb alt="불러오는 중" loading /></Spec>
          <Spec name="error" use="불러오기 실패"><PhotoThumb alt="실패 썸네일" error onRemove={() => undefined} /></Spec>
          <Spec name="disabled" use="비활성"><PhotoThumb src="/vite.svg" alt="비활성 썸네일" disabled onRemove={() => undefined} /></Spec>
        </Row>
        <p className="mt-2 text-xs text-text-secondary">
          sm 48 · md 64 · lg 96. onRemove 가 있으면 × 버튼, onOpen 이 있으면 썸네일 클릭으로 원본 보기. URL 생성·해제는 소비처 소유.
        </p>
      </Section>

      <Section title="앱 타일 · 공유 아이콘" hint="AppTile · IconCopy · IconShare · IconKakaoTalk (승인된 신규 2026-10-22)">
        <Sub>AppTile (§15.54) — tone · 상태</Sub>
        <Row top>
          <Spec name='tone="kakao"' use="카카오 공유 — 브랜드 색 토큰"><AppTile tone="kakao" icon={<IconKakaoTalk width={24} height={24} />} label="카카오톡" /></Spec>
          <Spec name='tone="kakao" disabled' use="카카오 키 없음 등 사용 불가"><AppTile tone="kakao" icon={<IconKakaoTalk width={24} height={24} />} label="카카오톡" disabled /></Spec>
          <Spec name='tone="default"' use="링크 복사"><AppTile icon={<IconCopy width={24} height={24} />} label="링크 복사" /></Spec>
          <Spec name='tone="default"' use="시스템 공유"><AppTile icon={<IconShare width={24} height={24} />} label="공유" /></Spec>
        </Row>
        <Sub>공유 아이콘 크기 (D-07 §20.1) — IconCopy · IconShare</Sub>
        <Row>
          <Spec name="16"><span className="text-text-secondary"><IconCopy width={16} height={16} /></span></Spec>
          <Spec name="20 (기본)"><span className="text-text-secondary"><IconCopy /></span></Spec>
          <Spec name="24"><span className="text-text-secondary"><IconShare width={24} height={24} /></span></Spec>
          <Spec name="32"><span className="text-text-secondary"><IconShare width={32} height={32} /></span></Spec>
        </Row>
        <p className="mt-2 text-xs text-text-secondary">
          아이콘 크기 16·20(기본)·24·32, stroke 2, 색은 currentColor. IconKakaoTalk 은 브랜드 로고라 stroke 규격 예외(채움 도형) — 브랜드 색은 AppTile tone=&quot;kakao&quot; 가 토큰으로 준다.
        </p>
      </Section>

      <Section title="공유하기 팝업" hint="ShareLinkPopup (shared-features/share-link) — 고객 링크 전용, props 주입">
        <Row>
          <ShareLinkPopupDemo />
        </Row>
        <p className="mt-2 text-xs text-text-secondary">
          링크 조회(Skeleton)·오류(Alert + 다시 시도)·복사 성공(&apos;복사됨 ✓&apos;, 팝업 유지) 상태 포함. 만료 문구는 서버 값만 표시.
        </p>
      </Section>

      <Section title="블록 캔버스" hint="§15.40 — CanvasPhotoRow + CanvasTextRow (WYSIWYG 본문 편집)">
        <BlockCanvasDemo />
        <p className="mt-2 text-xs text-text-secondary">
          행에 hover 시 컨트롤(순서 핸들·열 ±·＋설명·삭제) 노출. 사진 행 아래 문단은 상시 표시. 블록 모델·드래그·팔레트는 소비처 조립.
        </p>
      </Section>

      {/* 토스트 인스턴스 (.toast-wrap, z-index: --z-toast) */}
      <div className="toast-wrap">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            <span>{t.icon}</span>
            <span>{t.msg}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
